import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Mail } from 'lucide-react';
import { AnimatedBackground } from '../components/AnimatedBackground';
import { MilesLogo } from '../components/MilesLogo';
import { API_URL } from '../lib/api';
import { authClient } from '../lib/auth-client';
import {
  createRandomAuthGalleryColumns,
  getAuthGalleryImageUrl,
} from '../lib/auth-gallery';
import { AUTH_PAGE_CONTENT, type AuthMode } from '../lib/auth-page-config';
import {
  buildAuthCallbackUrl,
  buildAuthErrorCallbackUrl,
  getTeamInviteTokenFromLoginSearch,
  persistTeamInviteTokenForAuth,
} from '../lib/login-redirect';

// Build-time flag, set on the Cloud Dashboard service. The API enforces
// SIGNUPS_DISABLED separately; this shows the closed notice on signup and
// hides the "Create an account" link on login.
const SIGNUPS_DISABLED = import.meta.env.VITE_SIGNUPS_DISABLED === 'true';
const SIGNUPS_CLOSED_NOTICE =
  'New signups are closed. If you already have a Miles account, you can still sign in.';

const ERROR_MESSAGES: Record<string, string> = {
  rate_limited: 'Too many signup attempts. Please try again later.',
  // With signups closed, the API refuses new accounts and better-auth reports
  // it as a failed create. Explain why instead of inviting a retry.
  unable_to_create_user: SIGNUPS_DISABLED
    ? SIGNUPS_CLOSED_NOTICE
    : 'We could not create your account. Please try again or contact support.',
  ...(SIGNUPS_DISABLED && { failed_to_create_user: SIGNUPS_CLOSED_NOTICE }),
};

type SocialProvider = 'google' | 'github';

type LoginTeamInvitePreview = {
  accountName: string;
  email: string;
};

function GalleryWall() {
  const [columns] = useState(createRandomAuthGalleryColumns);
  // CSS hides the gallery on mobile, but the loading hint still needs to avoid
  // starting eager image requests for content that is not displayed.
  const isDesktopViewport =
    window.matchMedia?.('(min-width: 768px)').matches ?? false;

  return (
    <div className="absolute inset-x-0 -bottom-20 -top-20 flex gap-2">
      {columns.map((column, columnIndex) => (
        <div
          key={columnIndex}
          className={`flex flex-1 flex-col gap-2 ${
            columnIndex === 0 ? '-translate-y-10' : 'translate-y-8'
          }`}
        >
          {column.map(({ id, width, height }, tileIndex) => (
            <div
              key={id}
              className="shrink-0 rounded-[14px] border border-white/[0.1] bg-white/[0.045] p-1.5 shadow-[0_18px_60px_rgba(0,0,0,0.24)]"
            >
              <img
                src={getAuthGalleryImageUrl(id)}
                alt=""
                width={width}
                height={height}
                loading={
                  tileIndex === 0 && isDesktopViewport ? 'eager' : 'lazy'
                }
                decoding="async"
                className="block h-auto w-full rounded-[9px]"
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function LoadingIndicator() {
  return (
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="relative flex h-4 w-4 items-center justify-center">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/60" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-white/70" />
      </span>
    </span>
  );
}

function SocialAuthButton({
  mode,
  provider,
  loadingProvider,
  onClick,
}: {
  mode: AuthMode;
  provider: SocialProvider;
  loadingProvider: string | null;
  onClick: () => void;
}) {
  const label = provider === 'google' ? 'Google' : 'GitHub';
  const isLoading = loadingProvider === provider;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loadingProvider !== null}
      className="relative flex w-full items-center justify-center rounded-xl border border-white/[0.09] bg-white/[0.07] px-4 py-3 text-white transition-all duration-150 hover:border-white/[0.18] hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="flex items-center justify-center gap-3 font-medium">
        <span className={isLoading ? 'invisible' : ''}>
          {provider === 'google' ? <GoogleIcon /> : <GitHubIcon />}
        </span>
        <span className={isLoading ? 'invisible' : ''}>
          {AUTH_PAGE_CONTENT[mode].providerAction} with {label}
        </span>
      </span>
      {isLoading && <LoadingIndicator />}
    </button>
  );
}

function AuthPage({ mode }: { mode: AuthMode }) {
  const isSignup = mode === 'signup';
  const signupsClosed = isSignup && SIGNUPS_DISABLED;
  const content = AUTH_PAGE_CONTENT[mode];
  const [message, setMessage] = useState<{
    type: 'error' | 'success';
    text: string;
  } | null>(null);
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const [emailLogin, setEmailLogin] = useState('');
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [teamInviteToken] = useState(() =>
    isSignup ? null : getTeamInviteTokenFromLoginSearch(window.location.search),
  );
  const [teamInvitePreview, setTeamInvitePreview] =
    useState<LoginTeamInvitePreview | null>(null);
  // Server said the invite is gone (expired/revoked/used). Network failures
  // deliberately do NOT set this, so the UI does not mislabel a live invite.
  const [teamInviteInvalid, setTeamInviteInvalid] = useState(false);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    persistTeamInviteTokenForAuth(isSignup ? '' : window.location.search);
  }, [isSignup]);

  useEffect(() => {
    document.title = content.documentTitle;
  }, [content.documentTitle]);

  useEffect(() => {
    if (showEmailLogin) emailInputRef.current?.focus();
  }, [showEmailLogin]);

  useEffect(() => {
    if (!teamInviteToken) return;

    let cancelled = false;
    fetch(`${API_URL}/api/v2/cloud/team/invitations/${teamInviteToken}`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) {
          if (!cancelled) setTeamInviteInvalid(true);
          return null;
        }
        const data = await res.json();
        return data.invitation as LoginTeamInvitePreview;
      })
      .then((preview) => {
        if (!cancelled) setTeamInvitePreview(preview);
      })
      .catch(() => {
        if (!cancelled) setTeamInvitePreview(null);
      });

    return () => {
      cancelled = true;
    };
  }, [teamInviteToken]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (!error) return;

    setMessage({
      type: 'error',
      text: ERROR_MESSAGES[error] || 'Something went wrong. Please try again.',
    });
    params.delete('error');
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, []);

  async function handleSocialSignIn(provider: SocialProvider) {
    const label = provider === 'google' ? 'Google' : 'GitHub';
    setLoadingProvider(provider);
    setMessage(null);
    try {
      if (isSignup) persistTeamInviteTokenForAuth('');
      const callbackURL = buildAuthCallbackUrl(
        window.location.origin,
        window.location.search,
      );
      const { error } = await authClient.signIn.social({
        provider,
        callbackURL,
        ...(teamInviteToken ? { newUserCallbackURL: callbackURL } : {}),
        ...(teamInvitePreview?.email
          ? { loginHint: teamInvitePreview.email }
          : {}),
        errorCallbackURL: buildAuthErrorCallbackUrl(
          window.location.origin,
          window.location.search,
          isSignup ? '/signup' : '/login',
        ),
      });
      if (error) {
        console.error('[Auth] Social sign-in failed', {
          provider,
          status: error.status,
          message: error.message,
        });
        setMessage({
          type: 'error',
          text: `Could not connect to ${label}. Please try again.`,
        });
      }
    } catch {
      setMessage({
        type: 'error',
        text: `Could not connect to ${label}. Please try again.`,
      });
    } finally {
      setLoadingProvider(null);
    }
  }

  async function handleEmailSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = emailLogin.trim();
    if (!email) return;

    setLoadingProvider('email');
    setMessage(null);
    try {
      const callbackURL = buildAuthCallbackUrl(
        window.location.origin,
        window.location.search,
      );
      // better-auth resolves with { error } on HTTP failures instead of
      // throwing. Check the result before claiming that a link was sent.
      const { error } = await authClient.signIn.magicLink({
        email,
        callbackURL,
        ...(teamInviteToken
          ? {
              newUserCallbackURL: callbackURL,
              metadata: { teamInviteToken },
            }
          : {}),
        errorCallbackURL: buildAuthErrorCallbackUrl(
          window.location.origin,
          window.location.search,
        ),
      });
      if (error) {
        setMessage({
          type: 'error',
          text:
            error.status === 429
              ? 'Too many sign-in attempts. Wait a minute, then try again.'
              : 'Could not send a sign-in link. Please try again.',
        });
        return;
      }
      setMessage({
        type: 'success',
        text: teamInviteToken
          ? 'Check your email for a sign-in link. After you sign in, you will return to the invite.'
          : 'If that email has agency access, a sign-in link is on the way.',
      });
    } catch {
      setMessage({
        type: 'error',
        text: 'Could not send a sign-in link. Please try again.',
      });
    } finally {
      setLoadingProvider(null);
    }
  }

  const inputClass =
    'w-full rounded-xl border border-white/10 bg-white/[0.08] px-4 py-3.5 text-[15px] text-white placeholder:text-white/50 transition-colors focus:border-white/25 focus:bg-white/[0.12] focus:outline-none';

  return (
    <main className="relative flex h-dvh flex-col overflow-hidden md:flex-row">
      <AnimatedBackground />

      <div
        className="relative z-10 hidden h-full w-1/2 flex-col overflow-hidden px-6 md:flex"
        aria-hidden="true"
      >
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <GalleryWall />
        </div>
      </div>

      <div className="relative z-10 h-full flex-1 overflow-hidden md:w-1/2">
        <div className="absolute inset-0 grid place-items-center px-8 py-12">
          <div className="w-full max-w-sm">
            <div className="mb-9">
              <a href="https://bymiles.ai" className="inline-block">
                <MilesLogo size="lg" linked={false} />
              </a>
            </div>

            <h1 className="mb-2 text-2xl font-semibold tracking-[-0.02em] text-white">
              {teamInviteToken && !teamInviteInvalid
                ? 'Join Your Agency'
                : content.heading}
            </h1>

            {teamInviteToken && teamInviteInvalid ? (
              <div className="mb-6 mt-4 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm leading-relaxed">
                <p className="font-medium text-amber-300">
                  This invite link is no longer active.
                </p>
                <p className="mt-1 text-xs text-amber-300/70">
                  It may have expired or been revoked. Ask your agency to send a
                  new invitation. You can still log in below.
                </p>
              </div>
            ) : teamInviteToken ? (
              <div className="mb-6 mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-sm leading-relaxed">
                <p className="font-medium text-emerald-300">
                  You&apos;re invited to join
                  {teamInvitePreview?.accountName
                    ? ` ${teamInvitePreview.accountName}`
                    : ' an agency account'}
                  .
                </p>
                <p className="mt-1 text-xs text-emerald-300/70">
                  Use Google, GitHub, or email
                  {teamInvitePreview?.email ? (
                    <>
                      {' '}
                      as{' '}
                      <span className="break-all">
                        {teamInvitePreview.email}
                      </span>
                    </>
                  ) : (
                    ' with the invited email'
                  )}{' '}
                  to connect your account.
                </p>
              </div>
            ) : (
              <p className="mb-7 text-[15px] leading-relaxed text-white/55">
                {content.paragraph}
              </p>
            )}

            {message && (
              <div
                className={`mb-4 rounded-lg border p-3 text-center text-sm ${
                  message.type === 'success'
                    ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
                    : 'border-red-500/20 bg-red-500/10 text-red-400'
                }`}
                role="status"
              >
                {message.text}
              </div>
            )}

            {signupsClosed ? (
              <div
                className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-center text-sm leading-relaxed text-amber-200"
                role="status"
              >
                {SIGNUPS_CLOSED_NOTICE}
              </div>
            ) : (
              <div className="space-y-3">
                <SocialAuthButton
                  mode={mode}
                  provider="google"
                  loadingProvider={loadingProvider}
                  onClick={() => handleSocialSignIn('google')}
                />
                <SocialAuthButton
                  mode={mode}
                  provider="github"
                  loadingProvider={loadingProvider}
                  onClick={() => handleSocialSignIn('github')}
                />

                {content.showAgencyEmail && showEmailLogin ? (
                  <form onSubmit={handleEmailSignIn} className="flex gap-2">
                    <label htmlFor="email-login" className="sr-only">
                      Email
                    </label>
                    <input
                      ref={emailInputRef}
                      id="email-login"
                      type="email"
                      value={emailLogin}
                      onChange={(event) => setEmailLogin(event.target.value)}
                      placeholder="you@company.com"
                      autoComplete="email"
                      className={`${inputClass} h-12 min-w-0 flex-1`}
                    />
                    <button
                      type="submit"
                      disabled={loadingProvider !== null || !emailLogin.trim()}
                      className="flex h-12 min-w-20 items-center justify-center rounded-xl border border-white/[0.09] bg-white/[0.07] px-4 text-sm font-medium text-white transition-all duration-150 hover:border-white/[0.18] hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loadingProvider === 'email' ? 'Sending' : 'Send'}
                    </button>
                  </form>
                ) : content.showAgencyEmail ? (
                  <button
                    type="button"
                    onClick={() => setShowEmailLogin(true)}
                    disabled={loadingProvider !== null}
                    className="flex w-full items-center justify-center gap-3 rounded-xl border border-white/[0.09] bg-white/[0.07] px-4 py-3 font-medium text-white transition-all duration-150 hover:border-white/[0.18] hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Mail className="h-5 w-5" aria-hidden="true" />
                    Sign in with Agency Email
                  </button>
                ) : null}
              </div>
            )}

            {isSignup && !signupsClosed && (
              <p className="mt-5 text-center text-xs leading-relaxed text-white/45">
                We only use your name, email address, and profile picture to
                create your account.
              </p>
            )}

            {!teamInviteToken && !(SIGNUPS_DISABLED && !isSignup) && (
              <p className="mt-7 text-center text-sm text-white/45">
                {content.alternatePrompt}{' '}
                <a
                  href={`${content.alternatePath}${window.location.search}`}
                  className="font-medium text-white/80 underline decoration-white/25 underline-offset-4 transition-colors hover:text-white hover:decoration-white/60"
                >
                  {content.alternateLabel}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export function LoginPage() {
  return <AuthPage mode="login" />;
}

export function SignupPage() {
  return <AuthPage mode="signup" />;
}
